import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import {
  addCalibrationSession,
  validatealiateEmployeeCodes,
} from '../../../../store/apis/calibrationService';
import { initialValues, validationSchema } from './constants';
import './NewCalibrationForm.scss';
import people from '../../../../assets/images/calibration/people.png';
import { getAuthUser } from '../../../../utils/auth.util';
import { DatePicker } from '../../../../components/Common';

function NewCalibartionForm({
  isOpen,
  toggle,
  refetchCalibrationDetails,
  toggleSubject,
  togglePeople,
  toggleConfirmation,
  subjects,
  peoples,
  isSubjectValidated,
  selectedCalibration,
}) {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });
  const { userFullName } = getAuthUser();

  useEffect(() => {
    reset();
    if (selectedCalibration?.Session_Id) {
      setValue('sessionName', selectedCalibration.Session_Name);
      setValue(
        'sessionDate',
        selectedCalibration?.Session_Date?.trim()?.split('T')?.[0],
      );
      setValue('sessionSubject', selectedCalibration.Session_Subject);
      setValue('sessionOwner', selectedCalibration?.Session_Owner);
    } else {
      setValue('sessionOwner', userFullName);
    }
  }, [isOpen, reset, selectedCalibration, setValue, userFullName]);

  const { mutate: addSession, isLoading } = useMutation({
    mutationFn: addCalibrationSession,
    onSuccess: () => {
      toast.success('Session added successfully!', {
        className: 'toast-success',
      });
      refetchCalibrationDetails();
      toggle();
      reset();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to add session', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = async data => {
    if (!subjects || subjects.length <= 0) {
      toast.error('Please add at least one subject to proceed.', {
        className: 'toast-error',
      });
      return;
    }
    if (!peoples || peoples?.length <= 0) {
      toast.error('Please add at least one participant to proceed.', {
        className: 'toast-error',
      });
      return;
    }

    if (!isSubjectValidated) {
      toast.error('Please validate the subject before proceeding.', {
        className: 'toast-error',
      });
      return;
    }

    const postData = {
      employeeCodes: subjects?.map(item => item.EmpCd),
    };
    const empCodes = await validatealiateEmployeeCodes(postData);

    const validityCheck =
      (empCodes?.result?.length === subjects?.length &&
        empCodes?.result?.find(rs => rs?.IsValid === 'false')) ||
      empCodes?.result?.length < subjects?.length;

    if (validityCheck) {
      toast.error(
        'Some members have not yet reached the calibration stage. Please validate them before creating.',
        {
          className: 'toast-error',
        },
      );
      return;
    }

    const payload = {
      sessionId: selectedCalibration?.Session_Id ?? 0,
      sessionName: data.sessionName,
      sessionDate: data.sessionDate,
      sessionSubject: data.sessionSubject,
      sessionStatus: 'Scheduled',
      reviewUserId:
        subjects && subjects.length > 0
          ? subjects.map(item => item.EmpCd).join(',')
          : '',
      reviewerUserId:
        peoples && peoples.length > 0
          ? peoples.map(item => item.EmpCd).join(',')
          : '',
    };
    addSession(payload);
  };

  return (
    <div>
      <Offcanvas
        isOpen={isOpen}
        toggle={toggle}
        direction="end"
        className="calibration-oc"
        onClosed={() => {
          document.body.classList.remove('modal-open', 'offcanvas-open');
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        }}
        unmountOnClose
      >
        <OffcanvasHeader className="calibration-header">
          <div className="header">
            <span className="session-title">Schedule Session</span>
            <div className="header-add">
              {(!subjects || subjects?.length <= 0) && (
                <Button
                  className="btn custom-light-btn"
                  onClick={() => toggleSubject()}
                  outline
                >
                  Add Subjects
                  <img src={people} alt="" />
                </Button>
              )}
              {subjects && subjects.length > 0 && (
                <Button
                  className="btn custom-light-btn"
                  onClick={() => togglePeople()}
                  outline
                >
                  Add People
                  <img src={people} alt="" />
                </Button>
              )}
            </div>
          </div>
        </OffcanvasHeader>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="sub-title">
            <span> Basic Info</span>
          </div>
          <OffcanvasBody className="calibration-body">
            <Row className="gy-4">
              <Col md={12}>
                <div className="d-flex flex-column">
                  <Label>
                    Session Name<span className="text-danger"> *</span>
                  </Label>
                  <Controller
                    name="sessionName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter"
                        className="external-filter-input"
                      />
                    )}
                  />
                  {errors.sessionName && (
                    <span className="text-danger">
                      {errors.sessionName.message}
                    </span>
                  )}
                </div>
              </Col>
              <Col md={12}>
                <div className="d-flex flex-column">
                  <Label>
                    Session Date<span className="text-danger"> *</span>
                  </Label>
                  <Controller
                    name="sessionDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        className="external-filter-input"
                      />
                    )}
                  />
                  {errors.sessionDate && (
                    <span className="text-danger">
                      {errors.sessionDate.message}
                    </span>
                  )}
                </div>
              </Col>
              <Col md={12}>
                <div className="d-flex flex-column">
                  <Label>
                    Session Owner<span className="text-danger"> *</span>
                  </Label>
                  <Controller
                    name="sessionOwner"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        readOnly
                        type="text"
                        placeholder="Enter"
                        className="external-filter-input"
                      />
                    )}
                  />
                  {errors.sessionOwner && (
                    <span className="text-danger">
                      {errors.sessionOwner.message}
                    </span>
                  )}
                </div>
              </Col>
              <Col md={12}>
                <div className="d-flex flex-column">
                  <Label>
                    Session Subject<span className="text-danger"> *</span>
                  </Label>
                  <Controller
                    name="sessionSubject"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="textarea"
                        rows={3}
                        placeholder="Enter"
                        className={`comment-section external-filter-input ${errors.sessionSubject ? 'is-invalid' : ''}`}
                      />
                    )}
                  />
                  {errors.sessionSubject && (
                    <span className="text-danger">
                      {errors.sessionSubject.message}
                    </span>
                  )}
                </div>
              </Col>
            </Row>
          </OffcanvasBody>
          <div className="calibration-footer d-flex flex-row align-items-center gap-2 p-3">
            <div className="cbAction">
              <Col>
                <Button
                  type="button"
                  className="custom-light-btn w-100"
                  onClick={() => toggleConfirmation()}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="submit"
                  className="custom-btn w-100"
                  style={{
                    backgroundColor: '#017EBD',
                    border: '#017EBD',
                  }}
                  disabled={isLoading || !isValid}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
              </Col>
            </div>
          </div>
        </Form>
      </Offcanvas>
    </div>
  );
}

export default NewCalibartionForm;
